// Preset date ranges shown in the picker
export type DatePreset = {
  key: string;
  label: string;
  sublabel: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;
};

/** Returns the previous-calendar-month preset (recalculated at call time). */
export function getPreviousMonthPreset(): DatePreset {
  const now   = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end   = new Date(now.getFullYear(), now.getMonth(), 0);
  return buildPreset('previous_month', 'Previous month', start, end);
}

export function getLast30DaysPreset(): DatePreset {
  const now   = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - 29);
  return buildPreset('last_30_days', 'Last 30 days', start, now);
}

export function getLast7DaysPreset(): DatePreset {
  const now   = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - 6);
  return buildPreset('last_7_days', 'Last 7 days', start, now);
}

export function getThisYearPreset(): DatePreset {
  const now   = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return buildPreset('this_year', 'This year', start, now);
}

export function getPreviousWeekPreset(): DatePreset {
  const now   = new Date();
  // Previous ISO-style week: last Monday–Sunday
  const day   = now.getDay();                 // 0=Sun ... 6=Sat
  // Use Sun–Sat to match the MUI example's first-column Sunday header.
  const endSun = new Date(now);
  endSun.setDate(now.getDate() - day - 1);    // last Saturday
  const startPrev = new Date(endSun);
  startPrev.setDate(endSun.getDate() - 6);    // Sunday before that
  return buildPreset('previous_week', 'Previous week', startPrev, endSun);
}

export function getLast12MonthsPreset(): DatePreset {
  const now   = new Date();
  const start = new Date(now);
  start.setFullYear(now.getFullYear() - 1);
  start.setDate(start.getDate() + 1);
  return buildPreset('last_12_months', 'Last 12 months', start, now);
}

export function getPreviousYearPreset(): DatePreset {
  const now   = new Date();
  const start = new Date(now.getFullYear() - 1, 0, 1);
  const end   = new Date(now.getFullYear() - 1, 11, 31);
  return buildPreset('previous_year', 'Previous year', start, end);
}

function buildPreset(key: string, label: string, start: Date, end: Date): DatePreset {
  const fmt = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  return {
    key,
    label,
    sublabel:  `${fmt(start)} - ${fmt(end)}`,
    startDate: toYmd(start),
    endDate:   toYmd(end),
  };
}

export function buildCustomPreset(start: Date, end: Date): DatePreset {
  return buildPreset('custom', 'Custom range', start, end);
}

function toYmd(d: Date): string {
  // Avoid timezone shift from toISOString()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Quick-range chips shown in the date picker popover ──
export const QUICK_RANGES: Array<{ key: string; label: string; build: () => DatePreset }> = [
  { key: 'last_7_days',    label: 'Last 7 days',    build: getLast7DaysPreset },
  { key: 'last_30_days',   label: 'Last 30 days',   build: getLast30DaysPreset },
  { key: 'last_12_months', label: 'Last 12 months', build: getLast12MonthsPreset },
  { key: 'this_year',      label: 'This year',      build: getThisYearPreset },
  { key: 'previous_week',  label: 'Previous week',  build: getPreviousWeekPreset },
  { key: 'previous_month', label: 'Previous month', build: getPreviousMonthPreset },
  { key: 'previous_year',  label: 'Previous year',  build: getPreviousYearPreset },
];

// ── Compare-mode chips ──
export type CompareMode = 'previous_period' | 'same_period_last_year' | 'none';

export const COMPARE_MODES: Array<{ key: CompareMode; label: string }> = [
  { key: 'previous_period',       label: 'Previous period' },
  { key: 'same_period_last_year', label: 'Same period last year' },
  { key: 'none',                  label: 'No comparison' },
];